// Molecularix Research Library — single source of truth for compound knowledge entries.
// Add a new compound by appending an object here; the library pages, search, and
// category filters pick it up automatically. Keep everything evidence-based and
// RUO-compliant: no human-use, dosing, or therapeutic claims; no fabricated
// citations. References point to real, verifiable PubMed searches (and, for
// landmark work, a well-known citation). `verify: true` flags compounds whose
// scientific identity must be confirmed from authoritative documentation.

export const RESEARCH_AREAS = [
  "Metabolic Signaling",
  "Mitochondrial Biology",
  "Peptide Signaling",
  "Endocrine Signaling",
  "Growth-Factor Biology",
  "Neurobiology",
  "Cellular Stress",
  "Receptor Pharmacology",
  "Tissue Biology",
  "Muscle Biology",
  "Reproductive Signaling",
  "Oxidative Stress",
  "Vascular Biology",
  "Other / Emerging Research",
];

// pubmed helper term -> URL is built in the page component.
const pm = (term) => ({ term });

export const LIBRARY = [
  // ---------------- Metabolic & GLP-1 ----------------
  {
    slug: "aod-9604", productId: "aod-9604", name: "AOD-9604", catalog: "AOD-9604",
    aliases: ["AOD9604", "hGH fragment 176-191"],
    classification: "Modified synthetic fragment of human growth hormone (hGH 176-191).",
    evidence: "PRECLINICAL",
    areas: ["Metabolic Signaling", "Peptide Signaling"],
    targets: ["Growth hormone C-terminal fragment biology", "Adipose/lipid metabolism (experimental)"],
    overview: "AOD-9604 is a synthetic peptide based on the C-terminal region (residues 176-191) of human growth hormone. It has been investigated in preclinical models for its relationship to lipid and adipose metabolism, independent of the full growth-promoting activity of intact growth hormone.",
    mechanism: "Research has characterized the hGH 176-191 fragment in the context of lipolytic/lipid-metabolism pathways in experimental models. Mechanistic details remain under investigation.",
    studies: "Researchers have examined its effects on adipose tissue and lipid metabolism in cell and animal models.",
    limitations: "Most available evidence is preclinical; robust human efficacy data are limited. Commercial preparations may differ from peptides used in published studies.",
    keywords: ["growth hormone fragment", "adipose", "lipid", "metabolism", "obesity research"],
    refs: [pm("AOD9604 growth hormone fragment 176-191")],
  },
  {
    slug: "slu-pp-332", productId: "slu-pp-332", name: "SLU-PP-332", catalog: "SLU-PP-332",
    aliases: ["SLU-PP-332"],
    classification: "Small-molecule pan-ERR (estrogen-related receptor) agonist.",
    evidence: "PRECLINICAL",
    areas: ["Metabolic Signaling", "Mitochondrial Biology", "Muscle Biology"],
    targets: ["Estrogen-related receptors ERRα/β/γ", "Oxidative/mitochondrial metabolism"],
    overview: "SLU-PP-332 is an experimental small-molecule agonist of the estrogen-related receptors (ERRα/β/γ). It has been studied in preclinical models as an 'exercise-mimetic' tool compound in the context of oxidative and mitochondrial metabolism.",
    mechanism: "Activation of ERRs is associated with programs governing mitochondrial biogenesis and oxidative metabolism; SLU-PP-332 has been used to probe these pathways in experimental systems.",
    studies: "Preclinical research has examined effects on energy metabolism, oxidative muscle programs, and exercise-mimetic phenotypes in rodent and cell models.",
    limitations: "Evidence is early and preclinical; there is no established human efficacy. It is used as a research tool compound.",
    keywords: ["ERR", "estrogen related receptor", "mitochondria", "exercise mimetic", "oxidative metabolism"],
    refs: [pm("SLU-PP-332 estrogen-related receptor exercise mimetic")],
  },
  {
    slug: "retatrutide", productId: "retatrutide", name: "GLP-3 RETA (Retatrutide)", catalog: "GLP-3 RETA",
    aliases: ["Retatrutide", "LY3437943", "GLP-3 RETA"],
    classification: "Multi-receptor agonist peptide (GIP / GLP-1 / glucagon receptor).",
    evidence: "EARLY TRANSLATIONAL",
    areas: ["Metabolic Signaling", "Endocrine Signaling", "Receptor Pharmacology"],
    targets: ["GIP receptor", "GLP-1 receptor", "Glucagon receptor"],
    overview: "Retatrutide (catalogued as GLP-3 RETA) is an investigational triple incretin/glucagon-pathway agonist peptide acting at the GIP, GLP-1, and glucagon receptors. It has been the subject of published pharmacology and clinical-development research in metabolic science.",
    mechanism: "Simultaneous agonism of GIP, GLP-1, and glucagon receptors engages multiple metabolic-signaling pathways studied in incretin research.",
    studies: "Research has focused on receptor pharmacology and metabolic endpoints in translational and clinical-development settings.",
    limitations: "This is an investigational agent; content here is educational and not a treatment recommendation. Commercial research preparations may differ from clinical-grade material.",
    keywords: ["retatrutide", "incretin", "GIP", "GLP-1", "glucagon", "triple agonist", "metabolic"],
    refs: [
      { text: "Jastreboff AM et al. Triple-Hormone-Receptor Agonist Retatrutide for Obesity. N Engl J Med, 2023.", term: "retatrutide triple hormone receptor agonist obesity" },
    ],
  },
  {
    slug: "retatrutide-tirzepatide", productId: "retatrutide-tirzepatide", name: "GLP-3 RETA + GLP-2 TIRZ", catalog: "GLP-3 RETA + GLP-2 TIRZ",
    aliases: ["Retatrutide + Tirzepatide combination"],
    classification: "Combination research material (retatrutide + tirzepatide).", combination: true,
    evidence: "LIMITED / EMERGING",
    areas: ["Metabolic Signaling", "Endocrine Signaling", "Receptor Pharmacology"],
    targets: ["GIP receptor", "GLP-1 receptor", "Glucagon receptor"],
    overview: "This is a combination research material pairing retatrutide (a GIP/GLP-1/glucagon agonist) with tirzepatide (a GIP/GLP-1 agonist). Each component is individually described in incretin pharmacology literature.",
    mechanism: "See the individual entries for retatrutide and tirzepatide. The combination engages overlapping incretin/glucagon receptor pathways.",
    studies: "Research on the individual components exists; combination-specific literature is limited.",
    limitations: "Evidence for the individual components does NOT establish the behaviour or efficacy of the specific combination. Combination-specific data are limited.",
    keywords: ["combination", "retatrutide", "tirzepatide", "incretin", "blend"],
    refs: [pm("retatrutide"), pm("tirzepatide GIP GLP-1")],
    components: ["retatrutide", "tirzepatide"],
  },
  {
    slug: "tirzepatide", productId: "tirzepatide", name: "GLP-2 TIRZ (Tirzepatide)", catalog: "GLP-2 TIRZ",
    aliases: ["Tirzepatide", "LY3298176", "GLP-2 TIRZ"],
    classification: "Dual GIP / GLP-1 receptor agonist peptide.",
    evidence: "EARLY TRANSLATIONAL",
    areas: ["Metabolic Signaling", "Endocrine Signaling", "Receptor Pharmacology"],
    targets: ["GIP receptor", "GLP-1 receptor"],
    overview: "Tirzepatide (catalogued as GLP-2 TIRZ) is a dual GIP and GLP-1 receptor agonist peptide. It is significant in metabolic research as a tool for studying combined incretin-receptor pharmacology.",
    mechanism: "Co-agonism at GIP and GLP-1 receptors activates incretin signalling pathways central to metabolic research.",
    studies: "Research has examined GIP/GLP-1 receptor pharmacology and metabolic endpoints.",
    limitations: "Educational reference only; not a treatment recommendation. Research preparations may differ from clinical-grade material.",
    keywords: ["tirzepatide", "GIP", "GLP-1", "dual agonist", "incretin", "metabolic"],
    refs: [
      { text: "Frías JP et al. Tirzepatide versus Semaglutide Once Weekly (SURPASS-2). N Engl J Med, 2021.", term: "tirzepatide SURPASS GIP GLP-1" },
    ],
  },
  {
    slug: "tirzepatide-60mg", productId: "tirzepatide-60mg", name: "Tirzepatide (60 mg)", catalog: "Tirzepatide",
    aliases: ["Tirzepatide", "LY3298176"],
    classification: "Dual GIP / GLP-1 receptor agonist peptide.",
    evidence: "EARLY TRANSLATIONAL",
    areas: ["Metabolic Signaling", "Endocrine Signaling", "Receptor Pharmacology"],
    targets: ["GIP receptor", "GLP-1 receptor"],
    overview: "Tirzepatide is a dual GIP and GLP-1 receptor agonist peptide used in metabolic research to study combined incretin-receptor pharmacology. This entry corresponds to the higher-quantity catalog item.",
    mechanism: "Co-agonism at GIP and GLP-1 receptors activates incretin signalling pathways.",
    studies: "Research has examined GIP/GLP-1 receptor pharmacology and metabolic endpoints.",
    limitations: "Educational reference only; not a treatment recommendation.",
    keywords: ["tirzepatide", "GIP", "GLP-1", "dual agonist", "incretin"],
    refs: [{ text: "Frías JP et al. SURPASS-2. N Engl J Med, 2021.", term: "tirzepatide SURPASS" }],
  },
  {
    slug: "cagrilintide", productId: "cagrilintide", name: "Cagrilintide", catalog: "Cagrilintide",
    aliases: ["Cagrilintide", "AM833"],
    classification: "Long-acting amylin receptor agonist peptide.",
    evidence: "EARLY TRANSLATIONAL",
    areas: ["Metabolic Signaling", "Endocrine Signaling", "Receptor Pharmacology"],
    targets: ["Amylin receptors", "Calcitonin receptor family"],
    overview: "Cagrilintide is a long-acting amylin-receptor agonist peptide studied in metabolic-signalling research, often examined alongside incretin biology.",
    mechanism: "Agonism at amylin/calcitonin-family receptors engages satiety and metabolic-signalling pathways in experimental research.",
    studies: "Research has focused on amylin receptor pharmacology and metabolic endpoints.",
    limitations: "Educational reference only; human data remain limited relative to established incretin agents.",
    keywords: ["cagrilintide", "amylin", "metabolic", "satiety", "receptor"],
    refs: [pm("cagrilintide amylin analogue")],
  },
  {
    slug: "5-amino-1mq", productId: "5-amino-1mq", name: "5-Amino-1MQ", catalog: "5-Amino-1MQ",
    aliases: ["5-Amino-1-methylquinolinium", "5-Amino-1MQ"],
    classification: "Small-molecule NNMT (nicotinamide N-methyltransferase) inhibitor.",
    evidence: "PRECLINICAL",
    areas: ["Metabolic Signaling", "Cellular Stress"],
    targets: ["Nicotinamide N-methyltransferase (NNMT)"],
    overview: "5-Amino-1MQ is a small-molecule inhibitor of NNMT that has been used in preclinical metabolic research to probe NNMT-related pathways and cellular NAD/methylation biology.",
    mechanism: "Inhibition of NNMT is studied in the context of cellular metabolism and adipocyte biology in experimental models.",
    studies: "Preclinical research has examined NNMT inhibition and its metabolic consequences in cell and rodent models.",
    limitations: "Evidence is preclinical; human characterization is limited.",
    keywords: ["NNMT", "nicotinamide", "methyltransferase", "metabolism", "adipocyte"],
    refs: [pm("NNMT inhibitor 5-amino-1MQ metabolism")],
  },
  {
    slug: "tesamorelin", productId: "tesamorelin", name: "Tesamorelin", catalog: "Tesamorelin",
    aliases: ["Tesamorelin", "TH9507"],
    classification: "Growth-hormone-releasing hormone (GHRH) analog peptide.",
    evidence: "EARLY TRANSLATIONAL",
    areas: ["Endocrine Signaling", "Growth-Factor Biology", "Receptor Pharmacology"],
    targets: ["GHRH receptor"],
    overview: "Tesamorelin is a stabilized analog of growth-hormone-releasing hormone (GHRH) studied in endocrine research for its action on the GHRH receptor and the GH/IGF axis.",
    mechanism: "GHRH-receptor agonism stimulates endogenous growth-hormone-releasing pathways studied in endocrine research.",
    studies: "Research has examined GHRH-receptor signalling and downstream GH/IGF-axis endpoints.",
    limitations: "Educational reference only; not a treatment recommendation.",
    keywords: ["tesamorelin", "GHRH", "growth hormone releasing hormone", "endocrine"],
    refs: [pm("tesamorelin GHRH analog")],
  },
  // ---------------- Growth & Recovery ----------------
  {
    slug: "human-growth-hormone-hgh", productId: "human-growth-hormone-hgh", name: "HGH", catalog: "HGH",
    aliases: ["Human growth hormone", "Somatropin", "GH"],
    classification: "Endogenous 191-amino-acid peptide hormone (growth hormone).",
    evidence: "FOUNDATIONAL",
    areas: ["Endocrine Signaling", "Growth-Factor Biology", "Receptor Pharmacology"],
    targets: ["Growth hormone receptor (GHR)", "IGF-1 axis"],
    overview: "Human growth hormone is a well-characterized endogenous peptide hormone central to the GH/IGF-1 axis. Its biology is foundational in endocrinology and growth-factor research.",
    mechanism: "GH binds the growth hormone receptor, activating JAK/STAT signalling and downstream IGF-1 production, extensively described in the literature.",
    studies: "Research spans GH-receptor signalling, the IGF-1 axis, growth, and metabolism.",
    limitations: "Educational reference only; not a treatment recommendation. Research preparations may differ from clinical-grade material.",
    keywords: ["growth hormone", "somatropin", "GH", "IGF-1", "endocrine", "GHR"],
    refs: [pm("growth hormone receptor JAK STAT IGF-1 signaling")],
  },
  {
    slug: "bpc-157-tb-500-blend", productId: "bpc-157-tb-500-blend", name: "BPC-157 + TB-500 Blend", catalog: "BPC-157 + TB-500 Blend",
    aliases: ["BPC-157 + TB-500", "Body Protection Compound + Thymosin Beta-4 fragment"],
    classification: "Combination research material (BPC-157 + TB-500).", combination: true,
    evidence: "PRECLINICAL",
    areas: ["Tissue Biology", "Peptide Signaling"],
    targets: ["See individual components"],
    overview: "A combination research material pairing BPC-157 (a synthetic pentadecapeptide) with TB-500 (a thymosin beta-4-related peptide). Each component has its own experimental literature, largely preclinical.",
    mechanism: "Refer to the individual entries. Both components have been studied in tissue-biology contexts in animal models.",
    studies: "Research on the individual components exists; combination-specific data are limited.",
    limitations: "Evidence for individual components does NOT establish the behaviour of the specific combination. Most data are preclinical.",
    keywords: ["BPC-157", "TB-500", "combination", "tissue", "blend"],
    refs: [pm("BPC 157 pentadecapeptide"), pm("thymosin beta 4 TB-500")],
    components: ["bpc-157", "tb-500-thymosin-beta-4"],
  },
  {
    slug: "tb-500-thymosin-beta-4", productId: "tb-500-thymosin-beta-4", name: "TB-500 (Thymosin Beta-4)", catalog: "TB-500 (Thymosin Beta-4)",
    aliases: ["TB-500", "Thymosin beta-4", "Tβ4"],
    classification: "Thymosin beta-4-related peptide (actin-binding).",
    evidence: "PRECLINICAL",
    areas: ["Tissue Biology", "Peptide Signaling", "Vascular Biology"],
    targets: ["Actin cytoskeleton", "Cell migration pathways"],
    overview: "Thymosin beta-4 (Tβ4) is a naturally occurring actin-sequestering peptide involved in cell migration and tissue biology. Commercially labeled 'TB-500' products are frequently associated with Tβ4 or its active fragment; identity of a specific preparation should be distinguished from endogenous Tβ4.",
    mechanism: "Tβ4 binds G-actin and influences cytoskeletal dynamics and cell migration in experimental models.",
    studies: "Preclinical research has examined roles in cell migration, tissue repair models, and angiogenesis.",
    limitations: "Most evidence is preclinical. 'TB-500' commercial labeling may not be identical to endogenous Tβ4 used in studies.",
    keywords: ["TB-500", "thymosin beta 4", "actin", "cell migration", "tissue"],
    refs: [pm("thymosin beta 4 actin cell migration")],
  },
  {
    slug: "cjc-1295-ipamorelin", productId: "cjc-1295-ipamorelin", name: "CJC-1295 + Ipamorelin", catalog: "CJC-1295 + Ipamorelin",
    aliases: ["CJC-1295 + Ipamorelin"],
    classification: "Combination research material: GHRH analog (CJC-1295) + GH secretagogue (Ipamorelin).", combination: true,
    evidence: "PRECLINICAL",
    areas: ["Endocrine Signaling", "Growth-Factor Biology", "Receptor Pharmacology"],
    targets: ["GHRH receptor (CJC-1295)", "GH secretagogue / ghrelin receptor (Ipamorelin)"],
    overview: "A combination research material pairing CJC-1295 (a GHRH analog) with ipamorelin (a selective growth-hormone secretagogue). The two act on complementary pathways of GH-release research.",
    mechanism: "CJC-1295 engages the GHRH receptor while ipamorelin acts at the growth-hormone secretagogue (ghrelin) receptor; both are studied in GH-release research.",
    studies: "Research on the individual components addresses GH-release pathways; combination-specific literature is limited.",
    limitations: "Evidence for the individual components does not establish combination behaviour. Largely preclinical.",
    keywords: ["CJC-1295", "ipamorelin", "GHRH", "secretagogue", "growth hormone", "combination"],
    refs: [pm("CJC-1295 GHRH analog"), pm("ipamorelin growth hormone secretagogue")],
  },
  {
    slug: "ghrp-6", productId: "ghrp-6", name: "GHRP-6", catalog: "GHRP-6",
    aliases: ["GHRP-6", "Growth hormone releasing peptide 6"],
    classification: "Growth-hormone-releasing peptide / GH secretagogue.",
    evidence: "PRECLINICAL",
    areas: ["Endocrine Signaling", "Receptor Pharmacology"],
    targets: ["Growth-hormone secretagogue receptor (ghrelin receptor, GHS-R)"],
    overview: "GHRP-6 is a growth-hormone-releasing peptide studied for its action at the growth-hormone secretagogue (ghrelin) receptor in endocrine research.",
    mechanism: "Agonism at GHS-R stimulates GH-release pathways in experimental models.",
    studies: "Research has examined ghrelin-receptor pharmacology and GH-release endpoints.",
    limitations: "Largely preclinical characterization.",
    keywords: ["GHRP-6", "ghrelin receptor", "secretagogue", "growth hormone"],
    refs: [pm("GHRP-6 growth hormone secretagogue receptor")],
  },
  {
    slug: "igf-1-lr3", productId: "igf-1-lr3", name: "IGF-1 LR3", catalog: "IGF-1 LR3",
    aliases: ["Long R3 IGF-1", "IGF-1 LR3"],
    classification: "Modified insulin-like growth factor 1 analog (Long R3 construct).",
    evidence: "PRECLINICAL",
    areas: ["Growth-Factor Biology", "Receptor Pharmacology", "Muscle Biology"],
    targets: ["IGF-1 receptor", "IGF-binding-protein interactions (reduced)"],
    overview: "IGF-1 LR3 is a modified IGF-1 analog engineered with an extended N-terminus and an R3 substitution that reduces binding to IGF-binding proteins, used as a research construct to study IGF signalling.",
    mechanism: "Reduced IGFBP binding alters IGF-1 receptor engagement in experimental systems studying IGF signalling.",
    studies: "Research has used LR3 constructs to probe IGF-1 receptor signalling and cellular growth pathways.",
    limitations: "Preclinical/laboratory research construct; human characterization limited.",
    keywords: ["IGF-1", "LR3", "growth factor", "IGF receptor", "IGFBP"],
    refs: [pm("Long R3 IGF-1 IGF binding protein")],
  },
  {
    slug: "hexarelin-acetate", productId: "hexarelin-acetate", name: "Hexarelin Acetate", catalog: "Hexarelin Acetate",
    aliases: ["Hexarelin", "Examorelin"],
    classification: "Growth-hormone secretagogue hexapeptide.",
    evidence: "PRECLINICAL",
    areas: ["Endocrine Signaling", "Receptor Pharmacology"],
    targets: ["GH secretagogue receptor (GHS-R)", "CD36 (reported)"],
    overview: "Hexarelin is a synthetic growth-hormone secretagogue hexapeptide studied in endocrine and receptor-pharmacology research.",
    mechanism: "Acts at the GH secretagogue receptor; some literature also examines CD36 interactions in experimental settings.",
    studies: "Research has examined GH-release pathways and receptor pharmacology.",
    limitations: "Largely preclinical.",
    keywords: ["hexarelin", "secretagogue", "ghrelin receptor", "growth hormone"],
    refs: [pm("hexarelin growth hormone secretagogue")],
  },
  {
    slug: "kisspeptin-10", productId: "kisspeptin-10", name: "Kisspeptin-10", catalog: "Kisspeptin-10",
    aliases: ["Kisspeptin-10", "KISS1 fragment", "Metastin fragment"],
    classification: "Peptide fragment of the KISS1 gene product (kisspeptin).",
    evidence: "FOUNDATIONAL",
    areas: ["Reproductive Signaling", "Endocrine Signaling", "Neurobiology"],
    targets: ["Kisspeptin receptor (KISS1R / GPR54)"],
    overview: "Kisspeptin-10 is a bioactive fragment of kisspeptin, a key upstream regulator of the reproductive neuroendocrine axis via KISS1R (GPR54). Its biology is well established in reproductive endocrinology research.",
    mechanism: "KISS1R agonism regulates GnRH neurons and the hypothalamic-pituitary-gonadal axis in established research.",
    studies: "Research has examined KISS1/KISS1R signalling in reproductive and neuroendocrine contexts.",
    limitations: "Educational reference only; not a treatment recommendation.",
    keywords: ["kisspeptin", "KISS1R", "GPR54", "reproductive", "GnRH", "neuroendocrine"],
    refs: [pm("kisspeptin KISS1R GPR54 reproductive axis")],
  },
  {
    slug: "gdf-8", productId: "gdf-8", name: "GDF-8 (Myostatin)", catalog: "GDF-8 (Myostatin)",
    aliases: ["Myostatin", "GDF-8", "Growth differentiation factor 8"],
    classification: "TGF-β superfamily member (myostatin).",
    evidence: "FOUNDATIONAL",
    areas: ["Muscle Biology", "Growth-Factor Biology", "Receptor Pharmacology"],
    targets: ["Activin type II receptors (ActRIIB)", "SMAD signalling"],
    overview: "Myostatin (GDF-8) is a well-characterized TGF-β superfamily member that negatively regulates skeletal-muscle growth. Its biology is foundational in muscle research.",
    mechanism: "Signals through activin type II receptors and SMAD pathways to restrain muscle growth, as established in the literature.",
    studies: "Research spans myostatin regulation of muscle mass and its signalling pathways.",
    limitations: "Educational reference only.",
    keywords: ["myostatin", "GDF-8", "muscle", "activin receptor", "SMAD", "TGF-beta"],
    refs: [
      { text: "McPherron AC, Lawler AM, Lee SJ. Regulation of skeletal muscle mass by myostatin. Nature, 1997.", term: "myostatin GDF-8 skeletal muscle mass" },
    ],
  },
  {
    slug: "acth-1-39", productId: "acth-1-39", name: "ACTH 1-39", catalog: "ACTH 1-39",
    aliases: ["Adrenocorticotropic hormone", "Corticotropin", "ACTH 1-39"],
    classification: "39-amino-acid melanocortin peptide (full-length ACTH).",
    evidence: "FOUNDATIONAL",
    areas: ["Endocrine Signaling", "Receptor Pharmacology"],
    targets: ["Melanocortin receptors (esp. MC2R)", "HPA axis"],
    overview: "ACTH 1-39 is full-length adrenocorticotropic hormone, a foundational component of the hypothalamic-pituitary-adrenal (HPA) axis acting via melanocortin receptors.",
    mechanism: "ACTH activates melanocortin receptors (notably MC2R) to regulate adrenal steroidogenesis, extensively described in endocrinology.",
    studies: "Research spans melanocortin-receptor biology and HPA-axis regulation.",
    limitations: "Educational reference only; not a treatment recommendation.",
    keywords: ["ACTH", "corticotropin", "melanocortin", "MC2R", "HPA axis", "adrenal"],
    refs: [pm("ACTH melanocortin receptor HPA axis")],
  },
  // ---------------- Longevity & Cellular ----------------
  {
    slug: "ss-31", productId: "ss-31", name: "SS-31", catalog: "SS-31",
    aliases: ["SS-31", "Elamipretide", "MTP-131", "Bendavia"],
    classification: "Mitochondria-targeting tetrapeptide.",
    evidence: "EARLY TRANSLATIONAL",
    areas: ["Mitochondrial Biology", "Cellular Stress", "Oxidative Stress"],
    targets: ["Cardiolipin (inner mitochondrial membrane)"],
    overview: "SS-31 (elamipretide) is a mitochondria-targeting tetrapeptide that associates with cardiolipin on the inner mitochondrial membrane, studied in mitochondrial-biology and bioenergetics research.",
    mechanism: "Binding to cardiolipin is associated with stabilization of the inner mitochondrial membrane and improved bioenergetic function in experimental models.",
    studies: "Research spans mitochondrial dysfunction, oxidative stress, and bioenergetics in preclinical and some translational studies.",
    limitations: "Human data remain limited; much evidence is preclinical/early translational.",
    keywords: ["SS-31", "elamipretide", "mitochondria", "cardiolipin", "bioenergetics", "oxidative stress"],
    refs: [pm("elamipretide SS-31 cardiolipin mitochondria")],
  },
  {
    slug: "aicar", productId: "aicar", name: "AICAR", catalog: "AICAR",
    aliases: ["AICAR", "Acadesine", "AICA-riboside"],
    classification: "AMPK-activating nucleoside analog.",
    evidence: "PRECLINICAL",
    areas: ["Metabolic Signaling", "Mitochondrial Biology", "Muscle Biology"],
    targets: ["AMP-activated protein kinase (AMPK)"],
    overview: "AICAR is a widely used experimental AMPK activator employed as a metabolic research tool to probe energy-sensing pathways.",
    mechanism: "AICAR is metabolized to ZMP, which mimics AMP and activates AMPK, a central cellular energy sensor, in experimental systems.",
    studies: "Preclinical research has used AICAR to study AMPK signalling, oxidative metabolism, and exercise-mimetic phenotypes.",
    limitations: "Used as a laboratory tool compound; human characterization limited.",
    keywords: ["AICAR", "AMPK", "acadesine", "energy metabolism", "exercise mimetic"],
    refs: [pm("AICAR AMPK activator metabolism")],
  },
  {
    slug: "ghk-cu-copper-peptide", productId: "ghk-cu-copper-peptide", name: "GHK-Cu (Copper Peptide)", catalog: "GHK-Cu (Copper Peptide)",
    aliases: ["GHK-Cu", "Copper tripeptide-1", "Glycyl-L-histidyl-L-lysine copper"],
    classification: "Copper-binding tripeptide complex.",
    evidence: "PRECLINICAL",
    areas: ["Tissue Biology", "Peptide Signaling"],
    targets: ["Copper transport / cellular signalling (experimental)"],
    overview: "GHK-Cu is a naturally occurring copper-binding tripeptide (GHK) complexed with copper, studied in cellular and tissue-biology research and gene-expression models.",
    mechanism: "GHK-Cu is examined for copper delivery and effects on cellular signalling and gene-expression programs in experimental systems.",
    studies: "Preclinical research has examined roles in tissue-biology and gene-expression models.",
    limitations: "Most evidence is preclinical/in-vitro.",
    keywords: ["GHK-Cu", "copper peptide", "tripeptide", "tissue", "gene expression"],
    refs: [pm("GHK-Cu copper tripeptide gene expression")],
  },
  {
    slug: "glutathione", productId: "glutathione", name: "Glutathione", catalog: "Glutathione",
    aliases: ["Glutathione", "GSH", "γ-L-glutamyl-L-cysteinylglycine"],
    classification: "Endogenous tripeptide antioxidant (GSH).",
    evidence: "FOUNDATIONAL",
    areas: ["Oxidative Stress", "Cellular Stress"],
    targets: ["Cellular redox systems", "Glutathione peroxidase / reductase"],
    overview: "Glutathione (GSH) is a foundational intracellular tripeptide and a central component of the cellular redox and antioxidant system.",
    mechanism: "GSH participates in redox cycling and detoxification via glutathione peroxidase/reductase systems, extensively characterized.",
    studies: "Research spans redox biology, oxidative stress, and detoxification pathways.",
    limitations: "Educational reference only.",
    keywords: ["glutathione", "GSH", "antioxidant", "redox", "oxidative stress"],
    refs: [pm("glutathione redox antioxidant system")],
  },
  {
    slug: "mots-c", productId: "mots-c", name: "MOTS-c", catalog: "MOTS-c",
    aliases: ["MOTS-c", "Mitochondrial ORF of the 12S rRNA type-c"],
    classification: "Mitochondrial-derived peptide.",
    evidence: "PRECLINICAL",
    areas: ["Mitochondrial Biology", "Metabolic Signaling", "Cellular Stress"],
    targets: ["AMPK pathway", "Metabolic stress-response programs"],
    overview: "MOTS-c is a mitochondrial-derived peptide studied in metabolic-signalling and cellular-stress research, with reported links to AMPK-related pathways.",
    mechanism: "Research associates MOTS-c with AMPK signalling and metabolic stress-response programs in experimental models.",
    studies: "Preclinical research has examined metabolic regulation, mitochondrial biology, and stress-response signalling.",
    limitations: "Evidence is predominantly preclinical.",
    keywords: ["MOTS-c", "mitochondrial derived peptide", "AMPK", "metabolism", "mitochondria"],
    refs: [
      { text: "Lee C et al. The mitochondrial-derived peptide MOTS-c promotes metabolic homeostasis. Cell Metab, 2015.", term: "MOTS-c mitochondrial derived peptide metabolic" },
    ],
  },
  {
    slug: "nad", productId: "nad", name: "NAD+", catalog: "NAD+",
    aliases: ["NAD+", "Nicotinamide adenine dinucleotide"],
    classification: "Essential redox coenzyme.",
    evidence: "FOUNDATIONAL",
    areas: ["Mitochondrial Biology", "Metabolic Signaling", "Cellular Stress"],
    targets: ["NAD-dependent enzymes (sirtuins, PARPs)", "Cellular energy metabolism"],
    overview: "NAD+ is a foundational redox coenzyme central to cellular energy metabolism and a substrate for NAD-dependent enzymes such as sirtuins and PARPs.",
    mechanism: "NAD+/NADH cycling underpins bioenergetics; NAD+ also serves as substrate for sirtuin and PARP signalling, extensively characterized.",
    studies: "Research spans cellular energy metabolism, sirtuin biology, and NAD metabolism.",
    limitations: "Educational reference only.",
    keywords: ["NAD+", "nicotinamide adenine dinucleotide", "sirtuin", "PARP", "redox", "energy"],
    refs: [pm("NAD+ metabolism sirtuins cellular energy")],
  },
  {
    slug: "epithalon", productId: "epithalon", name: "Epithalon", catalog: "Epithalon",
    aliases: ["Epithalon", "Epitalon", "AEDG peptide", "Ala-Glu-Asp-Gly"],
    classification: "Synthetic tetrapeptide (AEDG).",
    evidence: "LIMITED / EMERGING",
    areas: ["Cellular Stress", "Other / Emerging Research"],
    targets: ["Telomere/telomerase biology (reported, experimental)"],
    overview: "Epithalon is a synthetic tetrapeptide (Ala-Glu-Asp-Gly) associated in experimental literature—largely from a limited set of research groups—with telomere and cellular-aging research.",
    mechanism: "Reported experimental effects on telomerase/telomere biology have been described; mechanisms remain incompletely characterized.",
    studies: "A limited body of experimental research has examined cellular-aging endpoints.",
    limitations: "Reported findings should be clearly distinguished from unsupported longevity claims; independent replication is limited.",
    keywords: ["epithalon", "epitalon", "AEDG", "telomere", "aging", "peptide"],
    refs: [pm("epitalon epithalon telomerase peptide")],
  },
  {
    slug: "dsip", productId: "dsip", name: "DSIP", catalog: "DSIP",
    aliases: ["DSIP", "Delta sleep-inducing peptide"],
    classification: "Endogenous neuropeptide (delta sleep-inducing peptide).",
    evidence: "LIMITED / EMERGING",
    areas: ["Neurobiology", "Peptide Signaling"],
    targets: ["Not fully characterized"],
    overview: "DSIP (delta sleep-inducing peptide) is a neuropeptide with a long research history in sleep- and stress-related neurobiology, though its mechanisms and receptor biology remain incompletely defined.",
    mechanism: "Proposed roles in sleep/stress-related signalling have been reported; a definitive receptor and mechanism remain uncertain.",
    studies: "Experimental research has examined sleep- and stress-related endpoints.",
    limitations: "Evidence is limited and older; mechanistic understanding remains uncertain.",
    keywords: ["DSIP", "delta sleep inducing peptide", "sleep", "stress", "neuropeptide"],
    refs: [pm("delta sleep inducing peptide DSIP")],
  },
  {
    slug: "selank", productId: "selank", name: "Selank", catalog: "Selank",
    aliases: ["Selank", "TP-7"],
    classification: "Synthetic heptapeptide (tuftsin analog).",
    evidence: "LIMITED / EMERGING",
    areas: ["Neurobiology", "Peptide Signaling"],
    targets: ["Tuftsin-related / neuromodulatory pathways (experimental)"],
    overview: "Selank is a synthetic peptide derived from the immunomodulatory peptide tuftsin, studied primarily in experimental neuropeptide research, much of it from Russian literature.",
    mechanism: "Experimental studies describe neuromodulatory and anxiolytic-related effects; mechanisms remain under investigation.",
    studies: "Experimental neuropeptide research has examined behavioural and neurochemical endpoints.",
    limitations: "The evidence base is limited and largely from a narrow set of sources.",
    keywords: ["selank", "tuftsin", "neuropeptide", "anxiolytic research", "neurobiology"],
    refs: [pm("selank tuftsin peptide anxiolytic")],
  },
  {
    slug: "semax", productId: "semax", name: "Semax", catalog: "Semax",
    aliases: ["Semax", "ACTH(4-7) analog"],
    classification: "Synthetic peptide (ACTH(4-7) analog).",
    evidence: "LIMITED / EMERGING",
    areas: ["Neurobiology", "Peptide Signaling"],
    targets: ["BDNF-related / neurotrophic pathways (reported)"],
    overview: "Semax is a synthetic peptide related to a fragment of ACTH studied in experimental neurobiology, with reported associations to neurotrophic signalling (e.g., BDNF) in research models, much of it from Russian literature.",
    mechanism: "Reported experimental effects on neurotrophic factors and neuromodulation; mechanisms remain under investigation.",
    studies: "Experimental research has examined neuroprotective and cognitive-related endpoints.",
    limitations: "Evidence is limited and largely from a narrow set of sources.",
    keywords: ["semax", "ACTH fragment", "BDNF", "neuroprotection", "neuropeptide"],
    refs: [pm("semax peptide BDNF neuroprotective")],
  },
  {
    slug: "ara-290", productId: "ara-290", name: "ARA-290 (Cibinetide)", catalog: "ARA-290 (Cibinetide)",
    aliases: ["ARA-290", "Cibinetide", "Innate repair receptor agonist"],
    classification: "Erythropoietin-derived peptide (non-erythropoietic).",
    evidence: "EARLY TRANSLATIONAL",
    areas: ["Tissue Biology", "Cellular Stress", "Peptide Signaling"],
    targets: ["Innate repair receptor (EPOR/CD131 complex)"],
    overview: "ARA-290 (cibinetide) is a peptide derived from the tissue-protective domain of erythropoietin that does not stimulate erythropoiesis, studied for the 'innate repair receptor' in tissue-protection research.",
    mechanism: "Engages the innate repair receptor complex associated with tissue-protective signalling in experimental and some translational studies.",
    studies: "Research has examined tissue-protective and anti-inflammatory endpoints.",
    limitations: "Human data remain limited; much evidence is preclinical/early translational.",
    keywords: ["ARA-290", "cibinetide", "erythropoietin", "innate repair receptor", "tissue protection"],
    refs: [pm("ARA 290 cibinetide innate repair receptor")],
  },
  {
    slug: "b7-33", productId: "b7-33", name: "B7-33", catalog: "B7-33",
    aliases: ["B7-33", "Single-chain relaxin peptide"],
    classification: "Single-chain relaxin-derived peptide (relaxin mimetic).",
    evidence: "PRECLINICAL",
    areas: ["Vascular Biology", "Tissue Biology", "Receptor Pharmacology"],
    targets: ["Relaxin family peptide receptor 1 (RXFP1)"],
    overview: "B7-33 is a single-chain relaxin-derived peptide described in the literature as a relaxin mimetic acting at RXFP1, studied in preclinical vascular and fibrosis models.",
    mechanism: "Engages RXFP1 signalling associated with relaxin biology in experimental models.",
    studies: "Preclinical research has examined vascular and anti-fibrotic endpoints.",
    limitations: "Evidence is preclinical; findings should not be interpreted as therapeutic claims.",
    keywords: ["B7-33", "relaxin", "RXFP1", "fibrosis", "vascular"],
    refs: [pm("B7-33 relaxin RXFP1 fibrosis")],
  },
  {
    slug: "crystagen", productId: "crystagen", name: "Crystagen", catalog: "Crystagen",
    aliases: ["Crystagen", "EDP", "Glu-Asp-Pro"],
    classification: "Short tripeptide bioregulator. Authoritative peer-reviewed literature maps Crystagen to Glu-Asp-Pro (EDP).",
    evidence: "LIMITED / EMERGING", verify: true,
    verifyNote: "Verified with qualification. Identity mapped to EDP (Glu-Asp-Pro) in a peer-reviewed review; the trade-name-to-material mapping remains pending an exact-lot analytical record and MS confirmation. Do NOT substitute KED (which the literature associates with Vesugen).",
    sequence: "Glu-Asp-Pro (EDP)", formula: "C14H21N3O8", mw: "~359.33 g/mol",
    cas: "Not established", pubchem: "PubChem CID 23624313 (EDP entity; not an independent 'Crystagen' synonym)",
    areas: ["Peptide Signaling", "Other / Emerging Research"],
    targets: ["Peptide bioregulator research (limited characterization)"],
    overview: "Crystagen is a short peptide bioregulator that authoritative literature identifies as the tripeptide Glu-Asp-Pro (EDP). Independent scientific characterization of biological activity remains limited.",
    mechanism: "A specific, independently characterized mechanism is not well established. Consult primary literature and product documentation.",
    studies: "The peptide-bioregulator literature is limited and should be interpreted cautiously.",
    limitations: "Identity is verified with qualification at the chemical level; product-level confirmation requires the supplier/exact-lot analytical record with MS/LC-MS identity and HPLC purity before an inventory lot is marked fully verified.",
    keywords: ["crystagen", "EDP", "Glu-Asp-Pro", "tripeptide", "bioregulator"],
    refs: [pm("Khavinson short peptides bioregulators Glu-Asp-Pro")],
  },
  {
    slug: "cortagen", productId: "cortagen", name: "Cortagen", catalog: "Cortagen",
    aliases: ["Cortagen", "AEDP", "Ala-Glu-Asp-Pro"],
    classification: "Short peptide bioregulator identified as the tetrapeptide Ala-Glu-Asp-Pro (AEDP).",
    evidence: "LIMITED / EMERGING",
    sequence: "Ala-Glu-Asp-Pro (AEDP)", formula: "C17H26N4O9", mw: "~430.4 g/mol",
    cas: "335591-03-2", pubchem: "PubChem CID 18439621 (verified chemical record)",
    areas: ["Peptide Signaling", "Other / Emerging Research"],
    targets: ["Peptide bioregulator research (limited characterization)"],
    overview: "Cortagen is a short peptide bioregulator identified as the tetrapeptide Ala-Glu-Asp-Pro (AEDP), with a verified PubChem chemical record.",
    mechanism: "Independent mechanistic characterization is limited. Consult primary literature and product documentation.",
    studies: "The available bioregulator literature is limited and should be interpreted cautiously.",
    limitations: "Chemical identity is verified; for a specific commercial lot, retain the supplier exact-lot analytical record and verify reported mass/purity against the reference identity.",
    keywords: ["cortagen", "AEDP", "Ala-Glu-Asp-Pro", "tetrapeptide", "bioregulator"],
    refs: [pm("Cortagen Ala-Glu-Asp-Pro peptide bioregulator")],
  },
  {
    slug: "bronchogen", productId: "bronchogen", name: "Bronchogen", catalog: "Bronchogen",
    aliases: ["Bronchogen", "AEDL", "Ala-Glu-Asp-Leu"],
    classification: "Short peptide bioregulator identified as the tetrapeptide Ala-Glu-Asp-Leu (AEDL).",
    evidence: "LIMITED / EMERGING",
    sequence: "Ala-Glu-Asp-Leu (AEDL)", formula: "C18H30N4O9", mw: "~446.5 g/mol",
    cas: "857267-12-0", pubchem: "PubChem CID 11690869 (verified chemical record)",
    areas: ["Peptide Signaling", "Other / Emerging Research"],
    targets: ["Peptide bioregulator research (limited characterization)"],
    overview: "Bronchogen is a short peptide bioregulator identified as the tetrapeptide Ala-Glu-Asp-Leu (AEDL), with a verified PubChem chemical record.",
    mechanism: "Independent mechanistic characterization is limited. Consult primary literature and product documentation.",
    studies: "The available bioregulator literature is limited and should be interpreted cautiously.",
    limitations: "Chemical identity is verified; for a specific commercial lot, retain the supplier exact-lot analytical record and verify reported mass/purity against the reference identity.",
    keywords: ["bronchogen", "AEDL", "Ala-Glu-Asp-Leu", "tetrapeptide", "bioregulator"],
    refs: [pm("Bronchogen Ala-Glu-Asp-Leu peptide bioregulator")],
  },
  {
    slug: "b-12", productId: "b-12", name: "B-12 (Methylcobalamin)", catalog: "B-12 (Methylcobalamin)",
    aliases: ["Methylcobalamin", "Vitamin B12", "Cobalamin"],
    classification: "Cobalamin (vitamin B12) cofactor.",
    evidence: "FOUNDATIONAL",
    areas: ["Metabolic Signaling", "Cellular Stress"],
    targets: ["Methionine synthase", "One-carbon / methylation metabolism"],
    overview: "Methylcobalamin is a bioactive form of vitamin B12, a foundational cofactor in one-carbon and methylation metabolism.",
    mechanism: "Serves as a cofactor for methionine synthase in methylation pathways, extensively characterized in biochemistry.",
    studies: "Research spans cobalamin biochemistry, methylation, and cellular metabolism.",
    limitations: "Educational reference only.",
    keywords: ["methylcobalamin", "vitamin B12", "cobalamin", "methylation", "cofactor"],
    refs: [pm("methylcobalamin vitamin B12 methionine synthase")],
  },
  {
    slug: "oxytocin-acetate", productId: "oxytocin-acetate", name: "Oxytocin Acetate", catalog: "Oxytocin Acetate",
    aliases: ["Oxytocin", "Oxytocin acetate"],
    classification: "Endogenous nonapeptide hormone.",
    evidence: "FOUNDATIONAL",
    areas: ["Neurobiology", "Endocrine Signaling", "Reproductive Signaling", "Receptor Pharmacology"],
    targets: ["Oxytocin receptor (OXTR)"],
    overview: "Oxytocin is a well-characterized endogenous nonapeptide hormone acting at the oxytocin receptor, with foundational roles in neuroendocrine and reproductive biology.",
    mechanism: "OXTR agonism engages neuroendocrine and reproductive signalling pathways, extensively described.",
    studies: "Research spans oxytocin-receptor pharmacology, neurobiology, and reproductive physiology.",
    limitations: "Educational reference only; not a treatment recommendation.",
    keywords: ["oxytocin", "OXTR", "neuroendocrine", "reproductive", "receptor"],
    refs: [pm("oxytocin receptor OXTR signaling")],
  },
  // ---------------- Specialty & Cosmetic ----------------
  {
    slug: "melanotan-i", productId: "melanotan-i", name: "Melanotan I", catalog: "Melanotan I",
    aliases: ["Melanotan I", "Afamelanotide", "NDP-α-MSH", "Melanotan-1"],
    classification: "α-MSH analog (melanocortin receptor agonist).",
    evidence: "PRECLINICAL",
    areas: ["Receptor Pharmacology", "Tissue Biology"],
    targets: ["Melanocortin-1 receptor (MC1R)"],
    overview: "Melanotan I is an analog of α-melanocyte-stimulating hormone (α-MSH) studied in melanocortin-receptor pharmacology and pigmentation research.",
    mechanism: "Agonism at melanocortin-1 receptor (MC1R) engages pigmentation-related signalling in experimental research.",
    studies: "Research has examined MC1R pharmacology and pigmentation biology.",
    limitations: "Educational reference only. This library does not provide tanning or human-use instructions.",
    keywords: ["melanotan", "alpha-MSH", "MC1R", "melanocortin", "pigmentation"],
    refs: [pm("melanotan alpha-MSH MC1R pigmentation")],
  },
  {
    slug: "dermorphin", productId: "dermorphin", name: "Dermorphin", catalog: "Dermorphin",
    aliases: ["Dermorphin"],
    classification: "Naturally occurring opioid heptapeptide.",
    evidence: "PRECLINICAL",
    areas: ["Neurobiology", "Receptor Pharmacology"],
    targets: ["Mu-opioid receptor (experimental)"],
    overview: "Dermorphin is a naturally occurring opioid peptide (originally identified from amphibian skin) studied in experimental research for its interaction with opioid receptors.",
    mechanism: "Experimental research characterizes dermorphin as a potent mu-opioid receptor agonist in laboratory models.",
    studies: "Research has examined opioid-receptor pharmacology in experimental systems.",
    limitations: "Educational reference only. This library does not provide recreational-use or human-use information.",
    keywords: ["dermorphin", "opioid peptide", "mu opioid receptor", "pharmacology"],
    refs: [pm("dermorphin opioid peptide mu receptor")],
  },
  {
    slug: "kpv", productId: "kpv", name: "KPV", catalog: "KPV",
    aliases: ["KPV", "Lysine-Proline-Valine", "α-MSH(11-13)"],
    classification: "Tripeptide derived from α-MSH (C-terminal fragment).",
    evidence: "PRECLINICAL",
    areas: ["Tissue Biology", "Peptide Signaling", "Other / Emerging Research"],
    targets: ["Inflammatory/cellular signalling (experimental)"],
    overview: "KPV is a tripeptide corresponding to the C-terminal fragment of α-MSH, studied in experimental research involving inflammatory and cellular-signalling pathways.",
    mechanism: "Experimental studies describe anti-inflammatory-associated cellular signalling; mechanisms remain under investigation.",
    studies: "Preclinical research has examined inflammatory and cellular-signalling endpoints.",
    limitations: "Experimental findings should be clearly distinguished from established clinical use; evidence is preclinical.",
    keywords: ["KPV", "alpha-MSH fragment", "inflammation", "tripeptide", "cellular signaling"],
    refs: [pm("KPV alpha-MSH tripeptide inflammation")],
  },
  {
    slug: "ptd-dbm", productId: "ptd-dbm", name: "PTD-DBM", catalog: "PTD-DBM",
    aliases: ["PTD-DBM"],
    classification: "25-residue protein-transduction-domain peptide (Dishevelled-binding motif), studied in Wnt-pathway research.",
    evidence: "PRECLINICAL", verify: true,
    verifyNote: "Chemical identity verified against PubChem/patent/analytical records. One PubChem CID mapping is inconsistent across sources (reported 176453931 vs 176453176) — confirm the database ID before final import. Product-level verification still requires an exact-lot analytical record with HPLC + MS/LC-MS.",
    sequence: "RRRRRRRRGGGGRKTGHQICKFRKC (25 aa)", formula: "C124H225N61O28S2", mw: "~3082.6 g/mol",
    cas: "1609454-11-6", pubchem: "PubChem CID reported 176453931 (database mapping pending confirmation)",
    areas: ["Receptor Pharmacology", "Tissue Biology", "Other / Emerging Research"],
    targets: ["Dishevelled (DVL) protein interaction / Wnt signalling (experimental)"],
    overview: "PTD-DBM is a 25-amino-acid peptide combining a polyarginine protein-transduction domain with a Dishevelled-binding motif, studied in preclinical Wnt-pathway research. Analytical records report a theoretical mass of ~3082.65 Da and an observed mass of ~3082.80 Da for the specified sequence.",
    mechanism: "Designed to engage the Dishevelled protein interaction within Wnt signalling; characterization is preclinical and experimental.",
    studies: "Preclinical research has examined Wnt-pathway/Dishevelled-related endpoints in experimental models.",
    limitations: "Evidence is preclinical. Confirm the PubChem database mapping and retain an exact-lot analytical record (HPLC + MS/LC-MS, lot number, testing laboratory) before an inventory lot is marked fully verified.",
    keywords: ["PTD-DBM", "Dishevelled", "Wnt", "protein transduction domain", "peptide"],
    refs: [pm("PTD-DBM Dishevelled Wnt protein transduction domain")],
  },
  {
    slug: "klow80-blend", productId: "klow80-blend", name: "KLOW80 Blend", catalog: "KLOW80 Blend",
    aliases: ["KLOW80", "KLOW blend"],
    classification: "Multi-component peptide BLEND — not a single chemical entity. No single formula, MW, or CAS applies.",
    combination: true, evidence: "LIMITED / EMERGING", verify: true,
    verifyNote: "KLOW80 is a four-component blend. Do NOT assign it a single molecular formula, molecular weight, CAS, or PubChem CID. Each component has its own verifiable identity; product-level verification requires an exact-lot analytical record (LC-MS/MS component identity + HPLC purity + component quantities).",
    blend: [
      { name: "GHK-Cu", amount: "50 mg", slug: "ghk-cu-copper-peptide" },
      { name: "BPC-157", amount: "10 mg", slug: null },
      { name: "TB-500 / Thymosin β4", amount: "10 mg", slug: "tb-500-thymosin-beta-4" },
      { name: "KPV", amount: "10 mg", slug: "kpv" },
    ],
    areas: ["Peptide Signaling", "Tissue Biology", "Other / Emerging Research"],
    targets: ["See individual components"],
    overview: "KLOW80 is a documented multi-component peptide blend with a nominal composition of GHK-Cu 50 mg + BPC-157 10 mg + TB-500 (Thymosin β4) 10 mg + KPV 10 mg (80 mg total). It is a formulation, not a single molecular entity.",
    mechanism: "Any mechanistic description depends on its individual components — see the component overviews below. Blend-specific characterization is limited.",
    studies: "Evidence exists for individual components; blend-specific literature is limited.",
    limitations: "Evidence for individual components does NOT establish the behaviour or efficacy of the blend. Product-level verification requires an exact-lot analytical record with LC-MS/MS component identity and HPLC purity.",
    keywords: ["KLOW80", "blend", "GHK-Cu", "BPC-157", "TB-500", "KPV", "combination"],
    refs: [],
    components: ["ghk-cu-copper-peptide", "tb-500-thymosin-beta-4", "kpv"],
  },
];

export const getEntry = (slug) => LIBRARY.find((e) => e.slug === slug) || null;
export const getEntryByProductId = (pid) => LIBRARY.find((e) => e.productId === pid) || null;
export const pubmedUrl = (term) => `https://pubmed.ncbi.nlm.nih.gov/?term=${encodeURIComponent(term)}`;
